import React, { useState, useEffect } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Building2, Plus, Phone, Mail, Users } from 'lucide-react';
import API from '@/api/client';
import { formatDateTime } from '@/utils/helpers';
import { toast } from 'sonner';

const CorporateInquiries = () => {
  const [loading, setLoading] = useState(true);
  const [inquiries, setInquiries] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    hr_contact_name: '',
    hr_contact_email: '',
    hr_contact_phone: '',
    employee_count: '',
    source: 'website',
  });

  useEffect(() => {
    fetchInquiries();
  }, [selectedStatus]);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const status = selectedStatus === 'all' ? '' : selectedStatus;
      const response = await API.get(`/admin/corporate-inquiries${status ? `?status=${status}` : ''}`);
      setInquiries(response.data.inquiries);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await API.post('/admin/corporate-inquiries', {
        ...formData,
        employee_count: parseInt(formData.employee_count),
      });
      toast.success('Inquiry created successfully');
      setShowNewDialog(false);
      setFormData({
        company_name: '',
        hr_contact_name: '',
        hr_contact_email: '',
        hr_contact_phone: '',
        employee_count: '',
        source: 'website',
      });
      fetchInquiries();
    } catch (error) {
      console.error('Error creating inquiry:', error);
      toast.error('Failed to create inquiry');
    }
  };

  const statusColors = {
    new: 'bg-blue-100 text-blue-800',
    contacted: 'bg-yellow-100 text-yellow-800',
    meeting_set: 'bg-purple-100 text-purple-800',
    pilot_proposed: 'bg-orange-100 text-orange-800',
    pilot_scheduled: 'bg-green-100 text-green-800',
    won: 'bg-green-600 text-white',
    lost: 'bg-gray-100 text-gray-800',
  };

  const statusFlow = ['new', 'contacted', 'meeting_set', 'pilot_proposed', 'pilot_scheduled', 'won'];

  const filteredInquiries = selectedStatus === 'all'
    ? inquiries
    : inquiries.filter(i => i.status === selectedStatus);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50 px-4 md:px-6 py-4">
        <div className="flex items-center gap-4">
          <img
            src="https://customer-assets.emergentagent.com/job_e65b2a0c-b7a8-46e9-8a92-d5df4207b5ae/artifacts/8vn39jol_Physiomobile%20Logo.jpeg"
            alt="Physiomobile Logo"
            className="h-10"
          />
          <h1 className="font-heading text-2xl font-bold text-gray-800">Flywheel HQ</h1>
        </div>
      </header>

      <main className="px-4 md:px-6 lg:px-8 py-8">
        <PageHeader
          title="Corporate Inquiry Pipeline"
          subtitle="Track B2B leads and pilot programs"
          actions={
            <div className="flex gap-2">
              <Button onClick={() => window.location.href = '/admin/dashboard'} variant="outline">
                Back to Dashboard
              </Button>
              <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="create-inquiry-button">
                    <Plus className="w-4 h-4 mr-2" />
                    New Inquiry
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Create Corporate Inquiry</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-4">
                    <div>
                      <Label>Company Name</Label>
                      <Input
                        value={formData.company_name}
                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>HR Contact Name</Label>
                        <Input
                          value={formData.hr_contact_name}
                          onChange={(e) => setFormData({ ...formData, hr_contact_name: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label>Employee Count</Label>
                        <Input
                          type="number"
                          value={formData.employee_count}
                          onChange={(e) => setFormData({ ...formData, employee_count: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={formData.hr_contact_email}
                        onChange={(e) => setFormData({ ...formData, hr_contact_email: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={formData.hr_contact_phone}
                        onChange={(e) => setFormData({ ...formData, hr_contact_phone: e.target.value })}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full">
                      Create Inquiry
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          }
        />

        {/* Status Filter Pills */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <Button
            variant={selectedStatus === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedStatus('all')}
          >
            All ({inquiries.length})
          </Button>
          {statusFlow.map((status) => (
            <Button
              key={status}
              variant={selectedStatus === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedStatus(status)}
            >
              {status.replace(/_/g, ' ').toUpperCase()}
            </Button>
          ))}
        </div>

        {loading ? (
          <LoadingSpinner size="lg" text="Loading inquiries..." />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredInquiries.map((inquiry, index) => (
              <Card
                key={inquiry.id}
                className="p-6 hover:shadow-lg transition-shadow"
                data-testid={`inquiry-card-${index}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-primary" />
                  </div>
                  <Badge className={statusColors[inquiry.status]}>
                    {inquiry.status.replace(/_/g, ' ').toUpperCase()}
                  </Badge>
                </div>

                <h3 className="font-heading text-xl font-semibold mb-2 text-gray-800">
                  {inquiry.company_name}
                </h3>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span>{inquiry.employee_count} employees</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span className="truncate">{inquiry.hr_contact_email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span>{inquiry.hr_contact_phone}</span>
                  </div>
                </div>

                <div className="text-xs text-muted-foreground mb-3">
                  Contact: {inquiry.hr_contact_name}
                </div>

                <div className="text-xs text-muted-foreground">
                  Created {formatDateTime(inquiry.created_at)}
                </div>
              </Card>
            ))}
          </div>
        )}

        {filteredInquiries.length === 0 && !loading && (
          <Card className="p-12 text-center">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="font-heading text-lg font-semibold text-gray-800 mb-2">
              No inquiries found
            </h3>
            <p className="text-muted-foreground mb-4">
              {selectedStatus === 'all'
                ? 'Create your first corporate inquiry to get started'
                : `No inquiries with status: ${selectedStatus}`}
            </p>
            <Button onClick={() => setShowNewDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Inquiry
            </Button>
          </Card>
        )}
      </main>
    </div>
  );
};

export default CorporateInquiries;